import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Component from '../../components/Component';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  getFanScanAnalytics,
  getFanScanDashboard,
  getFanScanErrorMessage,
  getFanScanPayload,
  isFanScanApiSuccess,
  isFanScanAuthError,
} from '../../services/fanScanApi';
import { validateDateRange } from './fanScanUtils';
import FanScanOverview from './FanScanOverview';
import FanScanAnalytics from './FanScanAnalytics';
import FanScanQuestions from './FanScanQuestions';
import FanScanResults from './FanScanResults';
import FanScanSeo from './FanScanSeo';

const TABS = ['overview', 'analytics', 'questions', 'results', 'seo'];

function FanScan() {
  const [searchParams, setSearchParams] = useSearchParams();
  const from = searchParams.get('from') || '';
  const to = searchParams.get('to') || '';
  const tab = TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'overview';
  const questionId = searchParams.get('question') || '';
  const categoryId = searchParams.get('category') || '';

  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const [rangeError, setRangeError] = useState('');
  const [dashLoading, setDashLoading] = useState(true);
  const [dashError, setDashError] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setDraftFrom(from);
    setDraftTo(to);
  }, [from, to]);

  const writeParams = useCallback(
    (next) => {
      const params = {};
      const nextTab = next.tab ?? tab;
      const nextQuestion = next.question === undefined ? questionId : next.question;
      const nextCategory = next.category === undefined ? categoryId : next.category;
      const nextFrom = next.from === undefined ? from : next.from;
      const nextTo = next.to === undefined ? to : next.to;
      if (nextTab && nextTab !== 'overview') params.tab = nextTab;
      if (nextQuestion) params.question = nextQuestion;
      if (nextCategory) params.category = nextCategory;
      if (nextFrom) params.from = nextFrom;
      if (nextTo) params.to = nextTo;
      setSearchParams(params);
    },
    [categoryId, from, questionId, setSearchParams, tab, to]
  );

  useEffect(() => {
    const problem = validateDateRange(from, to);
    if (problem) {
      setRangeError(problem);
      setDashLoading(false);
      setDashboard(null);
      return undefined;
    }
    setRangeError('');
    let cancelled = false;
    setDashLoading(true);
    getFanScanDashboard({ from, to }).then((res) => {
      if (cancelled) return;
      setDashLoading(false);
      if (isFanScanAuthError(res)) {
        window.location = '/login';
        return;
      }
      if (!isFanScanApiSuccess(res)) {
        setDashError(getFanScanErrorMessage(res, 'Failed to load Fan Scan.'));
        setDashboard(null);
        return;
      }
      setDashboard(getFanScanPayload(res));
      setDashError('');
    });
    return () => {
      cancelled = true;
    };
  }, [from, to, reloadKey]);

  useEffect(() => {
    if (tab !== 'analytics') return undefined;
    const problem = validateDateRange(from, to);
    if (problem) {
      setAnalytics(null);
      return undefined;
    }
    let cancelled = false;
    setAnalyticsLoading(true);
    getFanScanAnalytics({ from, to }).then((res) => {
      if (cancelled) return;
      setAnalyticsLoading(false);
      if (isFanScanAuthError(res)) {
        window.location = '/login';
        return;
      }
      if (!isFanScanApiSuccess(res)) {
        setAnalyticsError(getFanScanErrorMessage(res, 'Failed to load answer counts.'));
        setAnalytics(null);
        return;
      }
      setAnalytics(getFanScanPayload(res));
      setAnalyticsError('');
    });
    return () => {
      cancelled = true;
    };
  }, [tab, from, to, reloadKey]);

  const applyRange = () => {
    const problem = validateDateRange(draftFrom, draftTo);
    if (problem) {
      setRangeError(problem);
      return;
    }
    setRangeError('');
    writeParams({ from: draftFrom.trim(), to: draftTo.trim() });
  };

  const clearRange = () => {
    setDraftFrom('');
    setDraftTo('');
    setRangeError('');
    writeParams({ from: '', to: '' });
  };

  const range = (tab === 'analytics' ? analytics?.range : dashboard?.range) || dashboard?.range;
  const flagOff = dashboard?.flag_enabled === false;

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <Box sx={{ py: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h4">Fan Scan</Typography>
                <Typography variant="body2" color="text.secondary">
                  How the quiz is going, and the questions players see.
                </Typography>
              </Box>
              <Button
                startIcon={<RefreshIcon />}
                onClick={() => setReloadKey((value) => value + 1)}
                disabled={dashLoading}
              >
                Refresh
              </Button>
            </Stack>

            {flagOff ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Fan Scan is off. Players get a 404 until the app flag is turned on.
              </Alert>
            ) : null}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
              <TextField
                label="From"
                type="date"
                size="small"
                value={draftFrom}
                onChange={(event) => setDraftFrom(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="To"
                type="date"
                size="small"
                value={draftTo}
                onChange={(event) => setDraftTo(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button variant="contained" onClick={applyRange}>
                Apply
              </Button>
              <Button onClick={clearRange}>Last 30 days</Button>
              {dashboard?.flag_enabled != null ? (
                <Chip
                  size="small"
                  color={dashboard.flag_enabled ? 'success' : 'warning'}
                  label={dashboard.flag_enabled ? 'App flag on' : 'App flag off'}
                />
              ) : null}
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Dates are UTC and include both ends. Leave both empty for the last 30 days, including today.
              {range?.from && range?.to ? ` Active window: ${range.from} to ${range.to}.` : ''}
            </Typography>
            {rangeError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {rangeError}
              </Alert>
            ) : null}

            <Tabs
              value={tab}
              onChange={(_, value) => writeParams({ tab: value })}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2 }}
            >
              <Tab value="overview" label="Overview" />
              <Tab value="analytics" label="Analytics" />
              <Tab value="questions" label="Questions" />
              <Tab value="results" label="Results" />
              <Tab value="seo" label="SEO" />
            </Tabs>

            {tab === 'overview' && !rangeError ? (
              dashLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : dashError ? (
                <Alert severity="error">{dashError}</Alert>
              ) : (
                <FanScanOverview dashboard={dashboard} />
              )
            ) : null}

            {tab === 'analytics' && !rangeError ? (
              analyticsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : analyticsError ? (
                <Alert severity="error">{analyticsError}</Alert>
              ) : (
                <FanScanAnalytics analytics={analytics} />
              )
            ) : null}

            {tab === 'questions' ? (
              <FanScanQuestions
                selectedId={questionId}
                onSelect={(id) => writeParams({ tab: 'questions', question: id })}
                bundleStatus={dashboard?.bundle?.status}
                onSaved={() => setReloadKey((value) => value + 1)}
              />
            ) : null}

            {tab === 'results' ? (
              <FanScanResults
                selectedId={categoryId}
                onSelect={(id) => writeParams({ tab: 'results', category: id })}
                bundleStatus={dashboard?.bundle?.status}
                iconsById={Object.fromEntries(
                  (dashboard?.identities || []).map((identity) => [identity.id, identity.icon || ''])
                )}
              />
            ) : null}

            {tab === 'seo' ? <FanScanSeo /> : null}
          </Box>
        </div>
      </div>
    </Component>
  );
}

export default FanScan;
